/**
 * Territory: which completed city holds each tile (`territoryOwners`), with
 * level-1 discs first and grown rings claimed in upgrade order, so borders
 * are stable and identical on every peer. Placement, shelter, gathering
 * bonuses, automation and the borders all read it.
 */
import { axialHexDistance } from '../hexmap/coords';
import { getHexTileMapKey } from '../hexmap/model/keys';
import { hexesWithinRadius } from '../hexmap/model/map';
import type { HexTileMapKey } from '../hexmap/model/types';
import type { RuleSet } from '../rules/schema';
import { buildingLevel, levelStat } from './levels';
import type { Building, EntityId, PlayerId, SimState } from './types';

/** Mint order of an entity id ("b12" → 12): lower is older. */
function mintOrder(id: EntityId): number {
  return Number(id.slice(1));
}

/** Tiles a city claims: those with `from < distance <= to` from it. */
interface Claim {
  city: Building;
  from: number;
  to: number;
}

/**
 * Which city each territory tile belongs to (tile key → city id). Only
 * completed buildings with a `territoryRadius` count.
 *
 * First every city's level-1 disc, each tile to the nearest city covering it
 * (ties to the older city). Then the rings cities gained by levelling up, in
 * the order the upgrades happened (`levelTicks`; missing counts as tick 0):
 * each ring takes only tiles still free, so a growing city never takes land
 * another city already holds, and an earlier ring keeps its tiles when a rival
 * grows into them later. Rings from the same tick split by the same nearest
 * rule. A newly founded city's base disc still takes tiles from an existing
 * grown ring.
 */
function territoryOwners(state: SimState, rules: RuleSet): ReadonlyMap<HexTileMapKey, EntityId> {
  const sig = territorySignature(state, rules);
  const hit = ownersMemo.find((m) => m.rules === rules && m.sig === sig);
  if (hit) return hit.owners;
  const owners = computeOwners(state, rules);
  ownersMemo.unshift({ rules, sig, owners });
  if (ownersMemo.length > OWNERS_MEMO_SIZE) ownersMemo.pop();
  return owners;
}

/**
 * Recent `territoryOwners` results by rules and {@link territorySignature}.
 * The signature holds everything the partition reads, so a hit is exact even
 * across states (the host mutates one state in place; a client gets a new one
 * per snapshot; the UI also asks about a remembered "fog" state). A few
 * entries cover those alternating callers.
 */
const OWNERS_MEMO_SIZE = 4;
const ownersMemo: Array<{
  rules: RuleSet;
  sig: string;
  owners: ReadonlyMap<HexTileMapKey, EntityId>;
}> = [];

/**
 * Everything `territoryOwners` reads: each completed territory building's id,
 * tile, type, level and upgrade ticks, in state order.
 */
function territorySignature(state: SimState, rules: RuleSet): string {
  let sig = '';
  for (const b of Object.values(state.buildings)) {
    if (b.buildProgress < 1 || rules.buildings[b.type]?.territoryRadius === undefined) continue;
    sig += `${b.id}@${b.pos.q},${b.pos.r}:${b.type}:${b.level ?? 1}:${b.levelTicks?.join(',') ?? ''};`;
  }
  return sig;
}

function computeOwners(state: SimState, rules: RuleSet): Map<HexTileMapKey, EntityId> {
  const cities: Building[] = Object.values(state.buildings)
    .filter((b) => b.buildProgress >= 1 && rules.buildings[b.type]?.territoryRadius !== undefined)
    .sort((a, b) => mintOrder(a.id) - mintOrder(b.id) || (a.id < b.id ? -1 : 1));

  const owners = new Map<HexTileMapKey, EntityId>();
  const claim = (claims: Claim[]) => {
    const dist = new Map<HexTileMapKey, number>();
    const won = new Map<HexTileMapKey, EntityId>();
    for (const { city, from, to } of claims) {
      for (const coord of hexesWithinRadius(city.pos, to)) {
        const d = axialHexDistance(city.pos, coord);
        const key = getHexTileMapKey(coord);
        if (d <= from || owners.has(key)) continue;
        const best = dist.get(key);
        // Strictly nearer only: claims are listed oldest city first, so ties stay older.
        if (best !== undefined && d >= best) continue;
        dist.set(key, d);
        won.set(key, city.id);
      }
    }
    for (const [key, id] of won) owners.set(key, id);
  };

  claim(
    cities.map((city) => ({
      city,
      from: -1,
      to: rules.buildings[city.type]?.territoryRadius ?? 0,
    }))
  );

  const rings: Array<Claim & { tick: number }> = [];
  for (const city of cities) {
    const def = rules.buildings[city.type];
    if (!def) continue;
    for (let level = 2; level <= buildingLevel(city); level++) {
      rings.push({
        city,
        from: levelStat(def, level - 1, 'territoryRadius') ?? 0,
        to: levelStat(def, level, 'territoryRadius') ?? 0,
        tick: city.levelTicks?.[level - 2] ?? 0,
      });
    }
  }
  // Stable sort keeps oldest-city-first within a tick.
  rings.sort((a, b) => a.tick - b.tick);
  for (let i = 0; i < rings.length; ) {
    let j = i;
    while (j < rings.length && rings[j].tick === rings[i].tick) j++;
    claim(rings.slice(i, j));
    i = j;
  }
  return owners;
}

/**
 * Whether `b` is a city: a building type that holds territory (rules
 * `territoryRadius`), site or completed. Cities found and hold territory,
 * keep stock and keep their player in the match.
 */
function isCity(b: { type: string }, rules: RuleSet): boolean {
  return rules.buildings[b.type]?.territoryRadius !== undefined;
}

/** Tiles in `ownerId`'s territory (see {@link territoryOwners}). */
function computeTerritory(state: SimState, rules: RuleSet, ownerId: PlayerId): Set<HexTileMapKey> {
  const result = new Set<HexTileMapKey>();
  for (const [key, cityId] of territoryOwners(state, rules)) {
    if (state.buildings[cityId]?.owner === ownerId) result.add(key);
  }
  return result;
}

/**
 * Whether territory-bound building `b` (rules `placement.within: territory`:
 * farm, forestry, barracks) stands outside its owner's territory, e.g. after
 * a newer city's disc took its tile or its city fell. Such a building is idle
 * until the territory comes back: it yields nothing, takes no workers and
 * keeps no extra footprint tiles. Pass `owners` (from `territoryOwners`) when
 * calling for many buildings; it's only computed when needed.
 */
function outsideTerritory(
  state: SimState,
  rules: RuleSet,
  b: Building,
  owners?: ReadonlyMap<HexTileMapKey, EntityId>
): boolean {
  if (rules.buildings[b.type]?.placement?.within !== 'territory') return false;
  const cityId = (owners ?? territoryOwners(state, rules)).get(getHexTileMapKey(b.pos));
  return cityId === undefined || state.buildings[cityId]?.owner !== b.owner;
}

export { computeTerritory, isCity, mintOrder, outsideTerritory, territoryOwners };
